'use client';

import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@opaz/ui-components';
import {
  OTHER_REASON,
  USER_SATISFACTION_REASONS,
  userSatisfactionFormSchema,
  UserSatisfactionFormValues,
  UserSatisfactionReason,
} from '@/lib/schemas/userSatisfactionFormSchema';
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { useStrapiPost } from '@/hooks/useStrapiPost';
import LazyReCAPTCHA from '@/components/lazy-re-captcha';
import { useAnalytics } from '@/hooks/useAnalytics';

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

export default function UserSatisfactionForm({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation();

  const { mutateAsync, isSuccess, isError, error } = useStrapiPost('user-satisfactions');
  const analytics = useAnalytics();

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaTouched, setCaptchaTouched] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    control,
  } = useForm<UserSatisfactionFormValues>({
    resolver: zodResolver(userSatisfactionFormSchema),
    defaultValues: {
      reasons: [],
      additionalInfo: '',
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    },
  });

  const selectedReasons = useWatch({ name: 'reasons', control });
  const selectedReasonsSet = new Set(selectedReasons);

  const toggleReason = useCallback(
    (value: string) => {
      const newValues = selectedReasons.includes(value as UserSatisfactionReason)
        ? selectedReasons.filter((v: string) => v !== value)
        : [...selectedReasons, value];

      setValue('reasons', newValues as UserSatisfactionReason[]);

      if (value === OTHER_REASON && !newValues.includes(OTHER_REASON)) {
        setValue('additionalInfo', '');
      }
    },
    [selectedReasons, setValue],
  );

  const onSubmit = async (data: UserSatisfactionFormValues) => {
    if (!captchaToken) {
      setCaptchaTouched(true);
      return;
    }

    const payload = {
      pageContent: data.pageUrl,
      satisfied: false,
      reasons: data.reasons,
      comment: data.additionalInfo,
      captchaToken,
      skipRecaptcha: false,
    };

    try {
      await mutateAsync(payload);

      // Track survey_submit with the list of checked items (reasons)
      if (analytics) {
        analytics.trackSurveySubmit({
          reasons: data.reasons,
          hasComment: !!data.additionalInfo,
          commentLength: data.additionalInfo?.length || 0,
        });
      }

      // Also track report_issue for the reasons
      analytics.trackReportIssue(data.reasons);
    } catch (err) {
      // Error is already handled via "isError" and "error.message"
      console.error('Failed to submit user satisfaction:', err);
    }
  };

  return isSuccess ? (
    <p className="font-bold">{t('userSatisfaction.thankYou')}</p>
  ) : (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 max-w-xl px-4">
      <h2 className="text-xl font-bold text-foreground">{t('userSatisfaction.title')}</h2>
      <p className="text-foreground font-light">{t('userSatisfaction.disclaimer')}</p>

      <div className="flex flex-col gap-2">
        {USER_SATISFACTION_REASONS.map((reason) => (
          <label key={reason} className="flex items-center gap-2 mb-3 cursor-pointer">
            <input
              type="checkbox"
              value={reason}
              checked={selectedReasonsSet.has(reason)}
              onChange={() => toggleReason(reason)}
              className="w-6 h-6 rounded-xl"
            />
            <span>{t(`userSatisfaction.reasons.${reason}`)}</span>
          </label>
        ))}
        {errors.reasons && <p className="text-sm text-red-500">{errors.reasons.message}</p>}
      </div>

      {selectedReasonsSet.has(OTHER_REASON) && (
        <div className="flex flex-col gap-2">
          <label htmlFor="additionalInfo" className="font-medium text-sm">
            {t('userSatisfaction.commentLabel')}
          </label>
          <textarea
            rows={4}
            id="additionalInfo"
            {...register('additionalInfo')}
            placeholder={t('userSatisfaction.commentPlaceholder')}
            className="rounded-lg border p-3 border-base-border"
          />
          {errors.additionalInfo && <p className="text-sm text-red-500">{errors.additionalInfo.message}</p>}
        </div>
      )}

      <input type="hidden" {...register('pageUrl')} />

      <LazyReCAPTCHA
        ref={recaptchaRef}
        sitekey={SITE_KEY}
        onChange={(token) => {
          setCaptchaToken(token);
          setCaptchaTouched(false);
        }}
        onExpired={() => {
          setCaptchaToken(null);
          setCaptchaTouched(true);
        }}
      />

      {captchaTouched && !captchaToken && <p className="text-sm text-red-500">{t('common.errorMessageCaptcha')}</p>}

      {isError && error && <p className="text-sm text-red-500 mt-2">{t('common.errorMessagePostMethod')}</p>}

      <div className="flex gap-2">
        <Button type="submit" disabled={isSubmitting || !captchaToken}>
          {t('userSatisfaction.submit')}
        </Button>
        <Button className="bg-brand-xhigh" onClick={onCancel}>
          {t('userSatisfaction.cancel')}
        </Button>
      </div>
    </form>
  );
}
